/**
 * Client-side leaderboard API.
 *
 * Flow:
 *   1. startSession() at game start — server returns a signed token + sessionId.
 *   2. submitScore() at game over — server validates, persists if it qualifies.
 *   3. fetchLeaderboard() — public read of the top 10.
 *
 * In production this hits Netlify Functions. On localhost (or any environment
 * where `/api/*` doesn't exist) every call transparently falls back to a
 * localStorage-backed local leaderboard — so dev playtesting works without
 * spinning up `netlify dev`, and you don't lose your scores between reloads.
 */

export interface LeaderboardEntry {
  name: string;
  score: number;
  at: number; // epoch ms
}

export interface StartSessionResponse {
  sessionId: string;
  token: string;
  startedAt: number;
}

export interface SubmitScoreResponse {
  accepted: boolean;
  rank: number | null; // 1-based, null if didn't make top 10
  top: LeaderboardEntry[];
}

const API = "/api";
const LOCAL_TOP_KEY = "rosie-rumble:local-top10";
const TOP_N = 10;

/** Try to fetch JSON from the real API. Returns null if it doesn't exist,
 *  errors, or comes back as something other than JSON (e.g. Vite's SPA fallback HTML). */
async function tryRealJson<T>(url: string, init?: RequestInit): Promise<T | null> {
  try {
    const res = await fetch(url, init);
    if (!res.ok) return null;
    const ct = res.headers.get("content-type") ?? "";
    if (!ct.includes("application/json")) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export async function startSession(): Promise<StartSessionResponse | null> {
  const real = await tryRealJson<StartSessionResponse>(`${API}/start-session`, {
    method: "POST",
  });
  if (real && typeof real.sessionId === "string") return real;

  // Local fallback — the "local-" prefix tells submitScore to write straight to localStorage.
  return {
    sessionId: "local-" + Math.random().toString(36).slice(2, 10),
    token: "local",
    startedAt: Date.now(),
  };
}

export async function submitScore(args: {
  sessionId: string;
  token: string;
  name: string;
  score: number;
}): Promise<SubmitScoreResponse | null> {
  // Real session → hit the function.
  if (!args.sessionId.startsWith("local-")) {
    const real = await tryRealJson<SubmitScoreResponse>(`${API}/submit-score`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(args),
    });
    if (real) return real;
    // Real session but function unreachable — fall through to local so the user still sees something.
  }

  return submitToLocalBoard(args.name, args.score);
}

export async function fetchLeaderboard(): Promise<LeaderboardEntry[]> {
  const real = await tryRealJson<{ top: LeaderboardEntry[] }>(`${API}/leaderboard`);
  if (real && Array.isArray(real.top)) return real.top;
  return readLocalTop();
}

const NAME_KEY = "rosie-rumble:name";

export function loadStoredName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? "";
  } catch {
    return "";
  }
}

export function storeName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    /* ignore */
  }
}

export function sanitizeName(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9 _\-]/g, "").slice(0, 16).trim();
}

function readLocalTop(): LeaderboardEntry[] {
  try {
    const raw = localStorage.getItem(LOCAL_TOP_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocalTop(top: LeaderboardEntry[]): void {
  try {
    localStorage.setItem(LOCAL_TOP_KEY, JSON.stringify(top));
  } catch {
    /* ignore quota errors */
  }
}

function submitToLocalBoard(name: string, score: number): SubmitScoreResponse {
  const cleanName = sanitizeName(name) || "Anonymous";
  const current = readLocalTop();
  const entry: LeaderboardEntry = { name: cleanName, score, at: Date.now() };
  const next = [...current, entry]
    .sort((a, b) => b.score - a.score || a.at - b.at)
    .slice(0, TOP_N);
  const idx = next.findIndex(
    (e) => e.name === entry.name && e.score === entry.score && e.at === entry.at,
  );
  const rank = idx === -1 ? null : idx + 1;
  const accepted =
    rank !== null &&
    (current.length < TOP_N || score > (current[TOP_N - 1]?.score ?? 0));
  if (accepted) writeLocalTop(next);
  return { accepted, rank, top: next };
}
